import styled from "@emotion/styled";
import React from "react";
import { RaffleParticipationStatus } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import RaffleAvatar from "./RaffleAvatar";
import { default as FilledCircledTick } from "/public/assets/filled-circled-tick.svg?react";
import { default as FilledCircledX } from "/public/assets/filled-circled-x.svg?react";
import { default as RaffleTicketIcon } from "/public/assets/raffle-ticket.svg?react";

type Props = {
  tokens: number;
  shiftsToNextToken: number;
  shiftsPerToken: number;
  participationStatus: RaffleParticipationStatus;
  avatarUrl: string | null;
};

export default function RaffleMyTokensCard(props: Props) {
  const progressPercentage =
    ((props.shiftsPerToken - props.shiftsToNextToken) / props.shiftsPerToken) * 100;

  return (
    <Root>
      {props.participationStatus.isParticipating ? (
        <>
          <RaffleStatus>
            <FilledCircledTick />
            <span>{translate(STRINGS.YOURE_IN_THE_RAFFLE)}</span>
          </RaffleStatus>
          <RaffleHintText>
            {translate(STRINGS.COMPLETE_MORE_SHIFTS_TO_EARN_MORE_TOKENS)}
          </RaffleHintText>
        </>
      ) : (
        <>
          <RaffleStatus>
            <FilledCircledX />
            <span>{translate(STRINGS.YOURE_NOT_IN_THE_RAFFLE)}</span>
          </RaffleStatus>
          <RaffleHintText>
            {(() => {
              switch (props.participationStatus.reason) {
                case "NOT_ENOUGH_SHIFTS":
                  return translate(
                    STRINGS.YOU_NEED_TO_COMPLETE_MORE_SHIFTS_TO_ENTER_THE_RAFFLE
                  ).replace("{shifts}", props.participationStatus.numRequiredShiftsLeft.toString());

                case "NO_CALL_NO_SHOW":
                  return translate(STRINGS.YOU_MISSED_A_SHIFT_WITHOUT_NOTICE);

                case "NO_TOKENS":
                  return translate(STRINGS.YOU_HAVE_NO_TOKENS_COMPLETE_MORE_SHIFTS);

                case "RECENTLY_WON":
                  return translate(STRINGS.YOU_RECENTLY_WON_A_RAFFLE);
              }
            })()}
          </RaffleHintText>
        </>
      )}
      <AvatarWithTokensBadgeWrapper>
        <RaffleAvatar src={props.avatarUrl} />
        <RaffleTicketIconWrapper>
          <RaffleTicketIcon />
          <span>{props.tokens}</span>
        </RaffleTicketIconWrapper>
      </AvatarWithTokensBadgeWrapper>
      <ProgressBarContainer>
        <ProgressBar>
          <Progress progressPercentage={progressPercentage} />
        </ProgressBar>
      </ProgressBarContainer>
      <ProgressBarHintsContainer>
        <HintText>{translate(STRINGS.NEXT_TOKEN_IN)}</HintText>
        <Badge>
          {props.shiftsToNextToken} {translate(STRINGS.SHIFTS).toLowerCase()}
        </Badge>
      </ProgressBarHintsContainer>
    </Root>
  );
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.3);
  padding: 32px;
  border-radius: 8px;
  position: relative;
  text-align: center;
`;

const AvatarWithTokensBadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -32px;
  left: 50%;
  transform: translateX(-50%);
`;

const RaffleTicketIconWrapper = styled.div`
  position: relative;
  margin-inline-start: -16px;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    position: absolute;
    display: block;
    text-align: center;
    width: 100%;
    color: #fff;
    font-size: 20px;
    font-weight: 600;
  }
`;

const RaffleHintText = styled.div`
  color: var(--tw-gray-800);
  margin: 12px 0;
`;

const RaffleStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  margin-top: 28px;
  font-size: 18px;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  margin-bottom: 12px;
  gap: 24px;
`;

const ProgressBar = styled.div`
  position: relative;
  flex-grow: 1;
  height: 10px;
  background-color: #fff;
  border-radius: 5px;
`;

const Progress = styled.div<{ progressPercentage: number }>`
  width: ${({ progressPercentage }) => progressPercentage}%;
  height: 100%;
  background-color: var(--tw-indigo-500);
  border-radius: 5px;
`;

const ProgressBarHintsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
`;

const HintText = styled.div`
  color: var(--tw-gray-800);
`;

const Badge = styled.div`
  background-color: var(--tw-indigo-500);
  color: #fff;
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
`;
